/**
 * Dashboard React Query Hooks
 * Custom hooks for dashboard analytics with React Query
 */

import { useQuery } from '@tanstack/react-query';
import { analyticsService } from '../../../services/analytics.service';
import type { DashboardFilters } from '../../../types/analytics';

/**
 * Query Keys Factory
 */
export const dashboardKeys = {
  all: ['dashboard'] as const,
  analytics: () => [...dashboardKeys.all, 'analytics'] as const,
  analyticsWithFilters: (filters?: DashboardFilters) => 
    [...dashboardKeys.analytics(), filters] as const,
  chat: () => [...dashboardKeys.all, 'chat'] as const,
  chatStatistics: (filters?: Pick<DashboardFilters, 'date_from' | 'date_to'>) =>
    [...dashboardKeys.chat(), 'statistics', filters] as const,
  popularSubjects: (limit: number, filters?: Pick<DashboardFilters, 'date_from' | 'date_to'>) =>
    [...dashboardKeys.chat(), 'subjects', limit, filters] as const,
  frequentQuestions: (limit: number, minCount: number, approvedOnly: boolean) =>
    [...dashboardKeys.all, 'faq', limit, minCount, approvedOnly] as const,
};

/**
 * Hook: Get dashboard analytics
 * 
 * @param filters - Time period and date range filters
 * @returns Query result with dashboard analytics
 */
export function useDashboardAnalyticsQuery(filters?: DashboardFilters) {
  return useQuery({
    queryKey: dashboardKeys.analyticsWithFilters(filters),
    queryFn: () => analyticsService.getDashboardAnalytics(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 5, // Auto-refetch every 5 minutes
  });
}

/**
 * Hook: Get chat statistics
 * 
 * @param filters - Date range filters
 * @returns Query result with chat statistics
 */
export function useChatStatisticsQuery(
  filters?: Pick<DashboardFilters, 'date_from' | 'date_to'>
) {
  return useQuery({
    queryKey: dashboardKeys.chatStatistics(filters),
    queryFn: () => analyticsService.getChatStatistics(filters),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook: Get popular chat subjects
 * 
 * @param limit - Number of subjects to return
 * @param filters - Date range filters
 * @returns Query result with popular subjects
 */
export function usePopularSubjectsQuery(
  limit = 10,
  filters?: Pick<DashboardFilters, 'date_from' | 'date_to'>
) {
  return useQuery({
    queryKey: dashboardKeys.popularSubjects(limit, filters),
    queryFn: () => analyticsService.getPopularSubjects(limit, filters),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook: Get frequently asked questions
 * 
 * @param limit - Number of questions to return
 * @param minCount - Minimum occurrence count
 * @param approvedOnly - Only return approved questions
 * @returns Query result with frequent questions
 */
export function useFrequentQuestionsQuery(
  limit = 20,
  minCount = 2,
  approvedOnly = true
) {
  return useQuery({
    queryKey: dashboardKeys.frequentQuestions(limit, minCount, approvedOnly),
    queryFn: () => analyticsService.getFrequentQuestions(limit, minCount, approvedOnly),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}
